import type { RefObject } from "react";
import { RICH_TEXT_HINT } from "@/lib/document-builder/blocks/richTextSubset";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock, DocNestedBlock } from "@/lib/document-builder/blocks/blockTypes";
import { DocumentAdvancedBlockFields } from "./DocumentAdvancedBlockFields";

type EditableBlock = DocBlock | DocNestedBlock;

type Props = {
  block: EditableBlock;
  assets: DocAsset[];
  richTextRef?: RefObject<HTMLTextAreaElement | null>;
  onChange: (block: EditableBlock) => void;
  onRichTextWrap: (wrap: string) => void;
  onRichTextPrefix: (prefix: string) => void;
  onRichTextLink: () => void;
};

export function DocumentBlockFields(props: Props) {
  const { block, assets, onChange } = props;

  switch (block.type) {
    case "heading":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Level</span>
            <select
              value={block.level}
              onChange={(e) =>
                onChange({
                  ...block,
                  level: Number(e.target.value) as 1 | 2 | 3,
                })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            >
              <option value={1}>Heading 1</option>
              <option value={2}>Heading 2</option>
              <option value={3}>Heading 3</option>
            </select>
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Text</span>
            <input
              type="text"
              value={block.text}
              onChange={(e) => onChange({ ...block, text: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
        </div>
      );
    case "paragraph":
      return (
        <div className="space-y-1">
          <label className="block text-xs">
            <span className="text-gray-500">Text</span>
            <textarea
              value={block.text}
              onChange={(e) => onChange({ ...block, text: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">
            Use {"{{VariableKey}}"} for template variables.
          </p>
        </div>
      );
    case "rich_text":
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
              onClick={() => props.onRichTextWrap("**")}
            >
              Bold
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
              onClick={() => props.onRichTextWrap("*")}
            >
              Italic
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
              onClick={() => props.onRichTextPrefix("- ")}
            >
              List
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
              onClick={() => props.onRichTextLink()}
            >
              Link
            </button>
          </div>
          <textarea
            ref={props.richTextRef}
            value={block.body}
            onChange={(e) => onChange({ ...block, body: e.target.value })}
            rows={5}
            placeholder={RICH_TEXT_HINT}
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          />
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">{RICH_TEXT_HINT}</p>
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">
            Use {"{{VariableKey}}"} for template variables.
          </p>
        </div>
      );
    case "text_field":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Label</span>
            <input
              type="text"
              value={block.label}
              onChange={(e) => onChange({ ...block, label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Variable key</span>
            <input
              type="text"
              value={block.variable_key}
              onChange={(e) => onChange({ ...block, variable_key: e.target.value })}
              placeholder="CustomerName"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
        </div>
      );
    case "freeform_field":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Label</span>
            <input
              type="text"
              value={block.label}
              onChange={(e) => onChange({ ...block, label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Variable key</span>
            <input
              type="text"
              value={block.variable_key}
              onChange={(e) => onChange({ ...block, variable_key: e.target.value })}
              placeholder="SiteNotes"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">
            Multi-line fill field — appears in export fill and PDF as wrapped text.
          </p>
        </div>
      );
    case "formula_field":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Label</span>
            <input
              type="text"
              value={block.label}
              onChange={(e) => onChange({ ...block, label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Formula key</span>
            <input
              type="text"
              value={block.key}
              onChange={(e) => onChange({ ...block, key: e.target.value })}
              placeholder="Deposit"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Expression</span>
            <input
              type="text"
              value={block.expression}
              onChange={(e) => onChange({ ...block, expression: e.target.value })}
              placeholder="Price * 0.10"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">
            Reference variables and earlier formula keys (e.g. Price - Deposit).
          </p>
        </div>
      );
    case "image":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Asset</span>
            <select
              value={block.asset_id}
              onChange={(e) => onChange({ ...block, asset_id: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            >
              <option value="">Select from library…</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.display_name} ({asset.asset_type})
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Alt text</span>
            <input
              type="text"
              value={block.alt}
              onChange={(e) => onChange({ ...block, alt: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Caption (optional)</span>
            <input
              type="text"
              value={block.caption}
              onChange={(e) => onChange({ ...block, caption: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
        </div>
      );
    case "divider":
      return <hr className="border-gray-300 dark:border-zinc-600" />;
    case "page_break":
      return (
        <p className="rounded border border-dashed border-gray-300 px-3 py-2 text-center text-xs uppercase tracking-wide text-gray-500 dark:border-zinc-600 dark:text-zinc-400">
          Page break
        </p>
      );
    case "number_field":
    case "checkbox":
    case "signature_line":
    case "table":
    case "two_column":
    case "component_ref":
      return (
        <DocumentAdvancedBlockFields block={block} assets={assets} onChange={onChange} />
      );
    default: {
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}
